import PrototypePreviewNotice from './PrototypePreviewNotice';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Bell, BookOpen, CheckCircle2, ChevronRight,
  Coins, FileText, Film, Lightbulb, PenLine, Save,
  Send, Sparkles, Wand2,
} from 'lucide-react';

const QUICK_STARTS = [
  '我没有明确想法，先帮我找三个短剧方向',
  '我想写一个复仇爽剧，但不要太套路',
  '我有一句话设定，帮我变成能写的项目',
  '帮我设计一个第一集就有钩子的故事',
];

const CHIPS = [
  '仙侠逆袭',
  '现代甜宠',
  '悬疑反转',
  '都市异能',
  '职场爽剧',
  '复仇成长',
  '被背叛',
  '身份隐藏',
  '打脸',
  '追妻火葬场',
];

const DIRECTIONS = [
  {
    title: '仙门弃徒复仇',
    type: '仙侠逆袭 · 强爽感',
    story: '少年被宗门当成废棋逐出，却发现自己才是被封印的旧日剑主。',
    hook: '第一集结尾：他用废剑劈开宗门禁碑，碑中喊出他的旧名。',
    reason: '背叛、落差、身份悬念都能在第一集出现，适合快速抓住用户。',
  },
  {
    title: '替嫁女主反控全局',
    type: '甜宠反转 · 关系拉扯',
    story: '替嫁新娘本想逃婚，却发现病弱丈夫一直在暗中替她清算仇人。',
    hook: '第一集结尾：她刺杀失败，丈夫却把刀递回她手里，让她继续演。',
    reason: '关系张力强，甜宠和复仇可以并行推进。',
  },
  {
    title: '被裁员工证据反杀',
    type: '职场爽剧 · 现实压迫',
    story: '新人被高管栽赃背锅，意外拿到公司旧案证据链，开始逐层反杀。',
    hook: '第一集结尾：会议室大屏突然播放高管旧案录音。',
    reason: '现实代入强，冲突直接，适合做连续打脸。',
  },
];

function cx(...items) {
  return items.filter(Boolean).join(' ');
}

function Header() {
  const navigate = useNavigate();

  return (
    <header className="h-16 border-b border-black/[0.06] bg-white/88 backdrop-blur-xl sticky top-0 z-30">
      <div className="max-w-[1440px] mx-auto px-6 h-full flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/studio')}
            className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-stone-50"
          >
            <ArrowLeft size={17} />
          </button>
          <div className="flex items-center gap-2.5">
            <img src="/xiaoying-mark.svg" alt="小影" className="w-8 h-8 rounded-xl" />
            <span className="font-black text-stone-950">小影</span>
            <span className="px-2.5 py-1 rounded-full bg-[#f1edf8] text-[#6f6196] text-xs font-semibold">
              创意 Agent
            </span>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 text-xs text-stone-500">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-50 border border-black/[0.06]">
            <Save size={13} /> 示例对话
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700">
            <Coins size={13} /> 示例 736 点
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button disabled aria-describedby="prototype-static-controls" className="hidden sm:inline-flex px-4 py-2 rounded-full border border-black/10 text-xs font-semibold hover:bg-stone-50">
            我的创作
          </button>
          <button disabled aria-describedby="prototype-static-controls" className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-stone-50">
            <Bell size={16} />
          </button>
          <button disabled aria-describedby="prototype-static-controls" className="w-9 h-9 rounded-full bg-stone-950 text-white text-sm font-bold">A</button>
        </div>
      </div>
    </header>
  );
}

function Message({ role, children }) {
  const isUser = role === 'user';

  return (
    <div className={cx('flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="w-9 h-9 rounded-2xl bg-[#8c7db0] text-white flex items-center justify-center shrink-0 shadow-sm">
          <Sparkles size={16} />
        </div>
      )}
      <div
        className={cx(
          'max-w-[78%] rounded-[24px] px-5 py-4 text-sm leading-relaxed shadow-sm',
          isUser
            ? 'bg-stone-950 text-white rounded-br-md'
            : 'bg-white border border-black/[0.06] text-stone-800 rounded-bl-md'
        )}
      >
        {children}
      </div>
    </div>
  );
}

function DirectionOption({ direction, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        'w-full text-left rounded-2xl border p-4 transition',
        active
          ? 'bg-[#f1edf8] border-[#8c7db0]'
          : 'bg-white border-black/[0.06] hover:border-[#8c7db0]/50 hover:bg-[#fbfaf6]'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-black text-stone-950">{direction.title}</p>
          <p className="text-xs text-[#6f6196] mt-1">{direction.type}</p>
        </div>
        {active ? <CheckCircle2 size={18} className="text-[#8c7db0]" /> : <ChevronRight size={18} className="text-stone-300" />}
      </div>
      <p className="mt-3 text-sm text-stone-700 leading-relaxed">{direction.story}</p>
      <p className="mt-2 text-xs text-stone-500 leading-relaxed">为什么推荐：{direction.reason}</p>
    </button>
  );
}

function ChatArea({ selectedDirection, setSelectedDirection, input, setInput, addMessage, messages }) {
  return (
    <section className="min-h-[calc(100vh-96px)] flex flex-col rounded-[32px] border border-black/[0.06] bg-[#f8f5ef] overflow-hidden shadow-sm">
      <div className="px-6 py-5 border-b border-black/[0.06] bg-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#f1edf8] text-[#6f6196] text-xs font-semibold mb-3">
              <Lightbulb size={13} /> 不知道写什么也可以开始
            </div>
            <h1 className="text-3xl font-black text-stone-950 tracking-tight">
              和小影聊出一个能写下去的短剧方向
            </h1>
            <p className="text-sm text-stone-500 mt-2">
              你可以只说一句话，也可以点选题材。小影会不断帮你收敛到可进入写剧本的方案。
            </p>
          </div>
          <div className="hidden lg:block text-right text-xs text-stone-400">
            <p>当前阶段</p>
            <p className="mt-1 font-bold text-[#6f6196]">找方向 → 定方案</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
        {messages.map((message, index) => (
          <Message key={index} role={message.role}>{message.content}</Message>
        ))}

        <div className="pl-12 space-y-3">
          <Message role="agent">
            我先给你 3 个方向。你不用马上决定细节，先选一个“愿意继续写”的方向。
          </Message>
          <div className="grid gap-3 max-w-3xl">
            {DIRECTIONS.map(direction => (
              <DirectionOption
                key={direction.title}
                direction={direction}
                active={selectedDirection.title === direction.title}
                onClick={() => setSelectedDirection(direction)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="p-5 bg-white border-t border-black/[0.06]">
        <div className="flex flex-wrap gap-2 mb-3">
          {CHIPS.slice(0, 8).map(chip => (
            <button
              key={chip}
              onClick={() => setInput(prev => prev ? `${prev}，${chip}` : chip)}
              className="px-3 py-1.5 rounded-full bg-[#f7f4ee] hover:bg-[#eee9de] text-xs text-stone-600 border border-black/[0.05]"
            >
              {chip}
            </button>
          ))}
        </div>
        <div className="flex gap-3 items-end">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            rows={2}
            placeholder="比如：我想写一个被背叛后重生复仇的仙侠短剧……"
            className="flex-1 resize-none rounded-2xl border border-black/[0.08] bg-[#fbfaf6] px-4 py-3 text-sm outline-none focus:border-[#8c7db0]"
          />
          <button
            onClick={addMessage}
            className="w-12 h-12 rounded-2xl bg-[#8c7db0] hover:bg-[#7d6ca3] text-white flex items-center justify-center shadow-lg shadow-violet-300/30 shrink-0"
          >
            <Send size={17} />
          </button>
        </div>
      </div>
    </section>
  );
}

function SidePanel({ selectedDirection, input }) {
  const navigate = useNavigate();
  const completion = useMemo(() => {
    return Math.min(92, 54 + (input ? 18 : 0) + (selectedDirection ? 20 : 0));
  }, [input, selectedDirection]);

  const enterWriting = () => {
    const brief = [
      `项目名：${selectedDirection.title}`,
      `一句话故事：${selectedDirection.story}`,
      `第一集钩子：${selectedDirection.hook}`,
      `用户补充：${input || '暂无'}`,
    ].join('\n');
    const params = new URLSearchParams({ idea: selectedDirection.story, brief });
    navigate(`/writing?${params.toString()}`);
  };

  return (
    <aside className="space-y-4 lg:sticky lg:top-20">
      <section className="rounded-[32px] border border-black/[0.06] bg-white shadow-sm overflow-hidden">
        <div className="h-36 bg-[radial-gradient(circle_at_72%_34%,rgba(255,220,160,0.6),transparent_28%),linear-gradient(135deg,#17130f,#6b4230_52%,#111827)] relative">
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute left-5 bottom-5 text-white">
            <p className="text-xs text-white/68">当前方案</p>
            <h2 className="text-2xl font-black mt-1">{selectedDirection.title}</h2>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-stone-400">方案完整度</span>
              <span className="text-sm font-black text-[#6f6196]">{completion}%</span>
            </div>
            <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full rounded-full bg-[#8c7db0]" style={{ width: `${completion}%` }} />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold text-stone-400 mb-2">一句话故事</h3>
            <p className="text-sm text-stone-800 leading-relaxed">{selectedDirection.story}</p>
          </div>

          <div>
            <h3 className="text-xs font-bold text-stone-400 mb-2">第一集钩子</h3>
            <p className="text-sm text-stone-800 leading-relaxed">{selectedDirection.hook}</p>
          </div>

          <div>
            <h3 className="text-xs font-bold text-stone-400 mb-2">还需要补充</h3>
            <div className="grid grid-cols-2 gap-2">
              {['主角姓名', '核心对手', '情感关系', '结尾损失'].map(item => (
                <span key={item} className="px-3 py-2 rounded-2xl bg-[#f7f4ee] text-xs text-stone-600">
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button disabled aria-describedby="prototype-static-controls" className="py-3 rounded-2xl border border-black/[0.08] text-sm font-semibold hover:bg-stone-50">
              继续聊
            </button>
            <button disabled aria-describedby="prototype-static-controls" className="py-3 rounded-2xl border border-black/[0.08] text-sm font-semibold hover:bg-stone-50">
              换方向
            </button>
          </div>

          <button
            onClick={enterWriting}
            className="w-full py-3.5 rounded-2xl bg-stone-950 text-white text-sm font-black flex items-center justify-center gap-2 hover:bg-stone-800"
          >
            确认方案，进入写剧本 <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <section className="rounded-[28px] border border-black/[0.06] bg-white p-5 shadow-sm">
        <h3 className="font-black text-stone-950 mb-4 flex items-center gap-2">
          <Wand2 size={17} className="text-[#8c7db0]" /> 下一步会生成
        </h3>
        <div className="space-y-3 text-sm text-stone-600">
          {[
            [BookOpen, '故事圣经'],
            [FileText, '分集大纲'],
            [PenLine, '第一集正文'],
            [Film, '后续进入画布做视频'],
          ].map(([Icon, label]) => (
            <p key={label} className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Icon size={15} />{label}</span>
              <CheckCircle2 size={15} className="text-emerald-600" />
            </p>
          ))}
        </div>
      </section>
    </aside>
  );
}

function QuickStartPanel({ setInput, addPreset }) {
  return (
    <aside className="hidden xl:block space-y-4 lg:sticky lg:top-20">
      <section className="rounded-[28px] border border-black/[0.06] bg-white p-5 shadow-sm">
        <h3 className="font-black text-stone-950 flex items-center gap-2 mb-2">
          <Sparkles size={17} className="text-[#8c7db0]" /> 快速开局
        </h3>
        <p className="text-xs text-stone-500 leading-relaxed mb-4">
          没思路时直接点一句，小影会接着追问。
        </p>
        <div className="space-y-2">
          {QUICK_STARTS.map(item => (
            <button
              key={item}
              onClick={() => addPreset(item)}
              className="w-full text-left p-3 rounded-2xl bg-[#f7f4ee] hover:bg-[#eee9de] text-xs text-stone-700 leading-relaxed"
            >
              {item}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-[28px] border border-dashed border-black/[0.12] bg-white/70 p-5">
        <button
          onClick={() => setInput('我有参考剧本，想整理成一个原创短剧项目')}
          className="w-full py-3 rounded-2xl bg-white border border-black/[0.06] text-sm font-semibold text-stone-700 hover:bg-stone-50"
        >
          上传 / 粘贴参考资料
        </button>
      </section>
    </aside>
  );
}

function FlowBar() {
  return (
    <div className="max-w-[1440px] mx-auto px-6 pb-6">
      <div className="rounded-[24px] border border-black/[0.06] bg-white/78 backdrop-blur px-5 py-4 flex flex-wrap items-center gap-3 text-xs text-stone-500">
        {['创意方案', '写剧本', '剧本管理', '无限画布', '资产管理'].map((item, index) => (
          <div key={item} className="flex items-center gap-3">
            <span className={cx(
              'inline-flex items-center gap-2 px-3 py-2 rounded-full',
              index === 0 ? 'bg-[#f1edf8] text-[#6f6196] font-bold' : 'bg-[#f7f4ee]'
            )}>
              {index + 1}. {item}
            </span>
            {index < 4 && <ChevronRight size={14} className="text-stone-300" />}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PrototypeCreativeAgent() {
  const [input, setInput] = useState('一个被全宗门背叛的少年，重生后隐藏身份复仇');
  const [selectedDirection, setSelectedDirection] = useState(DIRECTIONS[0]);
  const [messages, setMessages] = useState([
    {
      role: 'agent',
      content: '你不用先想完整故事。先告诉我一个感觉、一种题材，或者点一个开局。我会帮你把它变成能写的短剧方向。',
    },
    {
      role: 'user',
      content: '我想写一个被背叛后重生复仇的故事。',
    },
    {
      role: 'agent',
      content: '这个方向适合短剧。关键是第一集必须立刻出现“被背叛的损失”和“重生后的反击机会”。我会先给你 3 个可选方向。',
    },
  ]);

  const addMessage = () => {
    const text = input.trim();
    if (!text) return;
    setMessages(prev => [
      ...prev,
      { role: 'user', content: text },
      {
        role: 'agent',
        content: '收到。我会保留这个设定，并继续帮你补齐主角、对手、核心冲突和第一集结尾钩子。',
      },
    ]);
    setInput('');
  };

  const addPreset = text => {
    setInput(text);
    setMessages(prev => [
      ...prev,
      { role: 'user', content: text },
      {
        role: 'agent',
        content: '可以，我们先不写正文。我先帮你找到 3 个短剧方向，再从里面选一个继续打磨。',
      },
    ]);
  };

  return (
    <div className="prototype-preview-shell"><PrototypePreviewNotice /><div className="prototype-preview-content"><div className="min-h-full bg-[#f6f2ea] text-stone-950">
      <Header />
      <div className="max-w-[1440px] mx-auto px-6 py-6 grid xl:grid-cols-[260px_minmax(0,1fr)_360px] gap-5 items-start">
        <QuickStartPanel setInput={setInput} addPreset={addPreset} />
        <ChatArea
          selectedDirection={selectedDirection}
          setSelectedDirection={setSelectedDirection}
          input={input}
          setInput={setInput}
          addMessage={addMessage}
          messages={messages}
        />
        <SidePanel selectedDirection={selectedDirection} input={input} />
      </div>
      <FlowBar />
    </div></div></div>
  );
}
